import React, { useMemo } from "react";
import {
  UserIcon,
  MapPinIcon,
  ShieldIcon,
  UserPlusIcon,
  DoctorIcon,
  PhoneIcon,
  AlertCircleIcon,
  MailIcon,
  IdCardIcon,
} from "../../../Icons";
import { formatPhone } from "../../Application/application.validation";
import {
  buildInsurances,
  buildPhysicians,
  buildDiagnosisAllergies,
  buildAllergies,
  formatAllergyNames,
} from "../patientForm.shared";
import { PATIENT_THEME } from "../patientTheme";
import StatusBadge from "./StatusBadge";
import PatientAvatar from "./PatientAvatar";

const dash = (value) => {
  const v = String(value ?? "").trim();
  return v || "—";
};

const formatPhoneSafe = (value) => {
  if (!value) return "—";
  return formatPhone(value) || value || "—";
};

const ROLE_ACCENT = {
  RN: "bg-indigo-500",
  LVN: "bg-indigo-500",
  CG: "bg-indigo-500",
  CNA: "bg-indigo-500",
  PT: "bg-indigo-500",
  PTA: "bg-indigo-500",
  OT: "bg-indigo-500",
  OTA: "bg-indigo-500",
  ST: "bg-indigo-500",
  HHA: "bg-indigo-500",
  MSW: "bg-indigo-500",
  CON: "bg-indigo-500",
  SRVY: "bg-indigo-500",
};

/** Fallback summary when API `summary` is missing. */
export const buildClientSummary = (patient = {}, assignedStaff = []) => {
  const patientName = [patient.lastName, patient.firstName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const street = [patient.address, patient.addressLine2]
    .filter((p) => p && String(p).trim())
    .join(", ");
  const cityPart = patient.city?.trim() || "";
  const stateZip = [patient.state, patient.zip]
    .filter((p) => p && String(p).trim())
    .join(" ");
  const locality = [cityPart, stateZip].filter(Boolean).join(", ");
  const addressLine = [street, locality].filter(Boolean).join(", ") || null;

  const primaryInsurance = buildInsurances(patient)[0];
  const primaryPhysician = buildPhysicians(patient)[0];
  const primaryDx = buildDiagnosisAllergies(patient)[0];
  const allergyName = formatAllergyNames(buildAllergies(patient)) || null;

  return {
    patientName: patientName || "Patient",
    mrn: patient.mrn || patient.patientNumber || null,
    patientNumber: patient.patientNumber || patient.mrn || null,
    phone: patient.phone || null,
    alternatePhone: patient.alternatePhone || null,
    email: patient.email || null,
    gender: patient.gender || null,
    dateOfBirth: patient.dateOfBirth || null,
    admissionDate: patient.admissionDate || null,
    eocDate: patient.eocDate || null,
    language: patient.language || null,
    primaryDiagnosis: primaryDx?.primaryDiagnosis || null,
    allergyName,
    clinicalGroup: primaryDx?.clinicalGroup || null,
    addressLine,
    insurance: primaryInsurance?.insuranceName || patient.insurance || null,
    policyNumber:
      primaryInsurance?.policyNumber || patient.insuranceNumber || null,
    physicianName:
      primaryPhysician?.physicianName || patient.primaryPhysician || null,
    physicianPhone:
      primaryPhysician?.physicianPhone ||
      patient.primaryPhysicianPhone ||
      null,
    physicianFax:
      primaryPhysician?.physicianFax || patient.primaryPhysicianFax || null,
    staffAssigned: (assignedStaff || [])
      .filter((s) => !s.status || s.status === "Active")
      .map((s) => ({
        name:
          [s.firstName, s.lastName].filter(Boolean).join(" ").trim() || "Staff",
        role: s.role || "",
        phone: s.mobileNumber || s.phone || null,
        email: s.email || null,
      })),
  };
};

const SECTION_TONES = {
  contact: PATIENT_THEME.summary,
  diagnosis: PATIENT_THEME.summary,
  location: PATIENT_THEME.summary,
  insurance: PATIENT_THEME.summary,
  physician: PATIENT_THEME.summary,
  care: PATIENT_THEME.summary,
};

const Section = ({ icon: Icon, title, tone = "contact", children, action }) => {
  const t = SECTION_TONES[tone] || SECTION_TONES.contact;
  return (
    <section className="border-b border-slate-100 last:border-b-0">
      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 ${t.head}`}>
        <span className={`w-0.5 h-3 rounded-full shrink-0 ${t.bar}`} aria-hidden />
        <Icon size={11} className={`shrink-0 ${t.icon}`} />
        <h3
          className={`text-[9px] font-bold uppercase tracking-wider flex-1 truncate ${t.title}`}
        >
          {title}
        </h3>
        {action}
      </div>
      <div className="px-2.5 py-2 space-y-2 bg-white">{children}</div>
    </section>
  );
};

const Field = ({ label, value, href, mono = false }) => {
  const display = value;
  const isEmpty = display === "—";
  const content = href && !isEmpty ? (
    <a
      href={href}
      className={`mt-0.5 block text-[12px] font-semibold leading-snug break-words hover:underline decoration-indigo-300 underline-offset-2 ${
        mono ? "tabular-nums text-slate-800" : "text-indigo-700"
      }`}
    >
      {display}
    </a>
  ) : (
    <div
      className={`mt-0.5 text-[12px] font-semibold leading-snug break-words ${
        isEmpty ? "text-slate-400 font-medium" : "text-slate-800"
      } ${mono ? "tabular-nums" : ""}`}
    >
      {display}
    </div>
  );

  return (
    <div className="min-w-0">
      <div className="text-[8px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      {content}
    </div>
  );
};

const PatientSummaryPanel = ({
  summary: summaryProp,
  patient,
  assignedStaff = [],
  className = "",
}) => {
  const clientSummary = buildClientSummary(patient, assignedStaff);
  const summary =
    summaryProp && summaryProp.patientName
      ? {
          ...clientSummary,
          ...summaryProp,
          mrn:
            summaryProp.mrn ||
            clientSummary.mrn ||
            patient?.mrn ||
            patient?.patientNumber ||
            null,
          patientNumber:
            summaryProp.patientNumber ||
            clientSummary.patientNumber ||
            patient?.patientNumber ||
            patient?.mrn ||
            null,
        }
      : clientSummary;

  const displayMrn = dash(
    summary.mrn || summary.patientNumber || patient?.mrn || patient?.patientNumber,
  );

  const staffList = useMemo(() => {
    if (Array.isArray(assignedStaff) && assignedStaff.length) {
      return assignedStaff
        .filter((s) => !s.status || s.status === "Active")
        .map((s) => ({
          name:
            [s.firstName, s.lastName].filter(Boolean).join(" ").trim() ||
            "Staff",
          role: s.role || "",
          phone: s.mobileNumber || s.phone || null,
          email: s.email || null,
        }));
    }
    return summary.staffAssigned || [];
  }, [assignedStaff, summary.staffAssigned]);

  const staffTotal = staffList.length;

  const phoneHref = summary.phone
    ? `tel:${String(summary.phone).replace(/\D/g, "")}`
    : undefined;
  const altPhoneHref = summary.alternatePhone
    ? `tel:${String(summary.alternatePhone).replace(/\D/g, "")}`
    : undefined;

  return (
    <aside className={`flex flex-col bg-white h-full min-h-0 ${className}`}>
      {/* Identity header — keep fully visible (no overflow clip on content) */}
      <div className="relative shrink-0 border-b border-slate-200">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-indigo-50/70" />
          <div className="absolute -right-6 -top-8 h-24 w-24 rounded-full bg-indigo-100/50 blur-2xl" />
        </div>
        <div className="relative z-[1] px-2.5 pt-2 pb-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span
              className="w-0.5 h-3 rounded-full bg-indigo-500 shrink-0"
              aria-hidden
            />
            <UserIcon size={11} className="text-indigo-600" />
            <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-700">
              Patient Summary
            </span>
          </div>

          <div className="flex items-start gap-2.5">
            <PatientAvatar
              firstName={patient?.firstName}
              lastName={patient?.lastName}
              size="sm"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <p
                  className="min-w-0 flex-1 text-[13px] font-bold text-slate-900 leading-snug tracking-tight break-words"
                  title={dash(summary.patientName)}
                >
                  {dash(summary.patientName)}
                </p>
                {patient?.status ? (
                  <div className="shrink-0">
                    <StatusBadge status={patient.status} compact />
                  </div>
                ) : null}
              </div>
              <p
                className="flex items-center gap-1 min-w-0 text-[10px] font-semibold text-slate-600 tabular-nums leading-none"
                title={displayMrn}
              >
                <IdCardIcon size={10} className="shrink-0 text-slate-400" />
                <span className="truncate">{displayMrn}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <Section icon={MapPinIcon} title="Location" tone="location">
          <Field label="Address" value={dash(summary.addressLine)} />
        </Section>

        <Section icon={PhoneIcon} title="Contact" tone="contact">
          <div className="space-y-2">
            <Field
              label="Primary Phone"
              value={formatPhoneSafe(summary.phone)}
              href={phoneHref}
              mono
            />
            <Field
              label="Alternate Phone"
              value={formatPhoneSafe(summary.alternatePhone)}
              href={altPhoneHref}
              mono
            />
          </div>
        </Section>

        <Section icon={AlertCircleIcon} title="Clinical" tone="diagnosis">
          <Field
            label="Primary Diagnosis"
            value={dash(summary.primaryDiagnosis)}
          />
          {summary.allergyName ? (
            <div className="min-w-0 py-0.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Allergies
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {String(summary.allergyName)
                  .split(",")
                  .map((part) => part.trim())
                  .filter(Boolean)
                  .map((name, idx) => (
                    <span
                      key={`${name}-${idx}`}
                      className="inline-flex items-center max-w-full px-2 py-0.5 rounded-md text-xs font-medium text-indigo-800 bg-indigo-50 border border-indigo-100/80 truncate"
                      title={name}
                    >
                      {name}
                    </span>
                  ))}
              </div>
            </div>
          ) : null}
          {summary.clinicalGroup ? (
            <Field label="Clinical Group" value={dash(summary.clinicalGroup)} />
          ) : null}
        </Section>

        <Section icon={ShieldIcon} title="Insurance" tone="insurance">
          <Field label="Provider" value={dash(summary.insurance)} />
          <Field label="Policy No." value={dash(summary.policyNumber)} mono />
        </Section>

        <Section icon={DoctorIcon} title="Physician" tone="physician">
          <Field label="Name" value={dash(summary.physicianName)} />
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Phone"
              value={formatPhoneSafe(summary.physicianPhone)}
              href={
                summary.physicianPhone
                  ? `tel:${String(summary.physicianPhone).replace(/\D/g, "")}`
                  : undefined
              }
              mono
            />
            <Field
              label="Fax"
              value={formatPhoneSafe(summary.physicianFax)}
              mono
            />
          </div>
        </Section>

        <Section
          icon={UserPlusIcon}
          title="Care Team"
          tone="care"
          action={
            <span className="inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 rounded-md bg-indigo-700 text-white text-[10px] font-bold tabular-nums">
              {staffTotal}
            </span>
          }
        >
          {staffTotal === 0 ? (
            <p className="text-[11px] text-slate-400 py-1">
              No staff assigned yet
            </p>
          ) : (
            <ul className="space-y-2 pr-0.5">
              {staffList.map((s, idx) => {
                const accent = ROLE_ACCENT[s.role] || "bg-slate-400";
                const staffTel = s.phone
                  ? `tel:${String(s.phone).replace(/\D/g, "")}`
                  : undefined;
                const staffMail = s.email ? `mailto:${s.email}` : undefined;
                return (
                  <li
                    key={`${s.role}-${s.name}-${idx}`}
                    className="rounded-lg border border-indigo-100/80 bg-gradient-to-br from-white to-indigo-50/40 px-2.5 py-2 min-w-0 shadow-[0_1px_0_rgba(15,23,42,0.03)]"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`shrink-0 w-1.5 h-1.5 rounded-full ${accent}`}
                        aria-hidden
                      />
                      <span className="text-[12px] font-semibold text-slate-900 truncate min-w-0">
                        {s.name}
                      </span>
                    </div>
                    <div className="mt-1.5 space-y-1 pl-3.5">
                      <p className="flex items-center gap-1.5 min-w-0 text-[11px] text-slate-600">
                        <PhoneIcon size={11} className="shrink-0 text-indigo-500" />
                        {staffTel ? (
                          <a
                            href={staffTel}
                            className="truncate tabular-nums hover:text-indigo-700 hover:underline"
                          >
                            {formatPhoneSafe(s.phone)}
                          </a>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </p>
                      <p className="flex items-center gap-1.5 min-w-0 text-[11px] text-slate-600">
                        <MailIcon size={11} className="shrink-0 text-indigo-500" />
                        {staffMail ? (
                          <a
                            href={staffMail}
                            className="truncate hover:text-indigo-700 hover:underline"
                          >
                            {s.email}
                          </a>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>
    </aside>
  );
};

export default PatientSummaryPanel;
